import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye, Loader2, Send } from "lucide-react";
import { MailPreview } from "@/components/EmailComposer";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { tk } from "@/i18n";

export const BULK_MAIL_MAX = 25;
const REASONS = { not_found: tk("Profile not found"), no_email: tk("No email permitted for recruiting outreach"), opted_out: tk("The candidate has not opted in to direct contact"), send_failed: tk("Sending failed") };
const SEL = "h-9 w-full rounded-md border bg-background px-2 text-sm";

function Recipients({ preview }) {
  const { t } = useI18n();
  if (!preview) return <div className="h-12 rounded-lg bg-muted shimmer" />;
  return (
    <div className="rounded-lg border p-3 text-xs space-y-2" data-testid="bulk-mail-recipients">
      <div><b data-testid="bulk-mail-eligible-count">{t("{{count}} recipients", { count: preview.eligible.length })}</b> · {t("from {{address}}", { address: preview.from })}</div>
      {preview.eligible.length === 0 && <p className="text-red-600" data-testid="bulk-mail-none-eligible">{t("None of the selected profiles has an email permitted for recruiting outreach.")}</p>}
      {preview.skipped.length > 0 && <div data-testid="bulk-mail-skipped"><div className="font-semibold text-amber-700">{t("Skipped ({{count}})", { count: preview.skipped.length })}</div>
        <div className="max-h-28 overflow-y-auto space-y-1 mt-1">{preview.skipped.map((s) => <div key={s.candidate_id} className="rounded bg-muted px-2 py-1"><b>{s.name}</b> — {t(REASONS[s.reason] || s.reason)}</div>)}</div></div>}
    </div>
  );
}

export function BulkEmailDialog({ ids, open, onOpenChange, onSent }) {
  const { setUpgradeOpen } = useAuth();
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [preview, setPreview] = useState(null);
  const [f, setF] = useState({ account_id: "", project_id: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const [pi, setPi] = useState(0);
  const showPreview = async () => {
    setBusy(true);
    try { const { data } = await call(true); setPreview(data); setPv(data.previews); setPi(0); } catch (e) { toast.error(errorDetail(e)); } finally { setBusy(false); }
  };
  const tooMany = ids.length > BULK_MAIL_MAX;
  const call = (dry, x = f) => api.post("/mail/send-bulk", { candidate_ids: ids, subject: x.subject || "-", body: x.body || "-", account_id: x.account_id || null, project_id: x.project_id || null, dry_run: dry });
  useEffect(() => {
    if (!open) return;
    setPreview(null); setPv(null);
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/mail/templates").then((r) => setTpls(r.data));
    api.get("/mail/status").then((r) => {
      setStatus(r.data);
      const acc = r.data.accounts.find((a) => a.status === "connected");
      if (acc && !tooMany) { const x = { ...f, account_id: acc.id }; setF(x); call(true, x).then((p) => setPreview(p.data)).catch((e) => toast.error(errorDetail(e))); }
    });
  }, [open]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const applyTpl = (id) => { const tpl = tpls.find((x) => x.id === id); if (tpl) setF({ ...f, subject: tpl.subject, body: tpl.body }); };
  const accounts = (status?.accounts || []).filter((a) => a.status === "connected");
  const send = async () => {
    if (!status?.can_send) return setUpgradeOpen(true);
    setBusy(true);
    try {
      const { data } = await call(false);
      toast.success(t("{{count}} emails sent", { count: data.sent.length }));
      if (data.failed.length) toast.error(t("{{count}} emails could not be sent", { count: data.failed.length }));
      onSent(); onOpenChange(false);
    } catch (e) { if (errorCode(e) === "upgrade_required") setUpgradeOpen(true); else toast.error(errorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-email-dialog">
        <DialogHeader><DialogTitle>{t("Email {{count}} profiles", { count: ids.length })}</DialogTitle><DialogDescription className="sr-only">{t("Each candidate receives a separate email addressed only to them — no other recipient is ever visible.")}</DialogDescription></DialogHeader>
        {tooMany ? <p className="text-sm text-red-600" data-testid="bulk-mail-too-many">{t("Maximum {{max}} profiles per sending. Reduce your selection.", { max: BULK_MAIL_MAX })}</p>
          : status && !accounts.length ? <p className="text-sm text-muted-foreground" data-testid="bulk-mail-no-mailbox">{t("Connect your mailbox first in")} <Link to="/app/messages" className="text-primary underline">{t("Messages")}</Link>.</p>
          : (
            <div className="space-y-3">
              <Recipients preview={preview} />
              <div className="grid grid-cols-2 gap-2">
                <select className={SEL} value={f.account_id} onChange={set("account_id")} data-testid="bulk-mail-account">{accounts.map((a) => <option key={a.id} value={a.id}>{a.address}</option>)}</select>
                <select className={SEL} value={f.project_id} onChange={set("project_id")} data-testid="bulk-mail-project"><option value="">{t("No project")}</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select>
              </div>
              <select className={SEL} defaultValue="" onChange={(e) => applyTpl(e.target.value)} data-testid="bulk-mail-template"><option value="">{t("Use a template…")}</option>{tpls.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}</select>
              <Input placeholder={t("Subject")} value={f.subject} onChange={set("subject")} data-testid="bulk-mail-subject" />
              <Textarea rows={8} placeholder={t("Write your message…")} value={f.body} onChange={set("body")} data-testid="bulk-mail-body" />
              <p className="text-[11px] text-muted-foreground">{t("Variables replaced for each profile:")} <span translate="no" className="font-mono">{"{prénom} {nom} {poste} {entreprise} {nom_recruteur} {entreprise_recruteur}"}</span> · {t("Your signature (Messages → Mailbox) is added automatically.")}</p>{/* i18n-ignore */}
              <p className="text-[11px] text-muted-foreground">{t("Each candidate receives a separate email addressed only to them — no other recipient is ever visible.")}</p>
              <Button className="w-full" disabled={busy || !preview?.eligible.length || !f.subject.trim() || !f.body.trim()} onClick={showPreview} data-testid="bulk-mail-preview">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}{t("Preview")}
              </Button>
            </div>
          )}
        {pv && (
          <div className="absolute inset-0 bg-background rounded-lg p-6 flex flex-col gap-3 overflow-y-auto" data-testid="bulk-mail-preview-pane">
            <div className="flex items-center justify-between"><div className="text-lg font-semibold">{t("Preview")}</div>
              <div className="flex items-center gap-1 text-xs" data-testid="bulk-mail-preview-nav">
                <Button size="sm" variant="outline" className="h-7 px-2" disabled={pi <= 0} onClick={() => setPi(pi - 1)} data-testid="bulk-mail-preview-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <span>{t("Recipient {{n}} of {{total}}", { n: pi + 1, total: pv.length })}</span>
                <Button size="sm" variant="outline" className="h-7 px-2" disabled={pi >= pv.length - 1} onClick={() => setPi(pi + 1)} data-testid="bulk-mail-preview-next"><ChevronRight className="w-4 h-4" /></Button>
              </div></div>
            {pv[pi] && <MailPreview from={preview.from} to={`${pv[pi].name} <${pv[pi].to}>`} subject={pv[pi].subject} body={pv[pi].body} />}
            <div className="flex gap-2 mt-auto">
              <Button variant="outline" className="flex-1" onClick={() => setPv(null)} data-testid="bulk-mail-back">{t("Edit")}</Button>
              <Button className="flex-1" disabled={busy} onClick={send} data-testid="bulk-mail-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{t("Confirm sending to {{count}} profiles", { count: pv.length })}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
